import { lazy, Suspense } from 'react'
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom'
import { DndProvider } from 'react-dnd'
import { dndManager } from '@/dnd-manager'
import { setAppRouter } from '@/services/app-router'
import { setCustomerIoPath } from '@/services/customerio'

import { ErrorBoundary } from '@/components/common/error-boundary'
import { AuthGuard } from '@/features/auth/components/auth-guard'
import { BillingGuard } from '@/features/auth/components/billing-guard'
import { HomeRedirect } from '@/features/auth/components/home-redirect'
import { AppLayout } from '@/components/layout/app-layout'
import { LoginPage } from '@/features/auth/pages/login-page'
import { RegisterPage } from '@/features/auth/pages/register-page'
import { CobraPage } from '@/features/auth/pages/cobra-page'
import { PasswordResetPage } from '@/features/auth/pages/password-reset-page'
import { VerifyEmailPage } from '@/features/auth/pages/verify-email-page'
import { BetaLockedPage } from '@/features/auth/pages/beta-locked-page'
import { BETA, CALENDLY } from '@/config/constants'
// Route-level backstop for flagged surfaces (nav filtering hides the entry;
// this stops a pasted URL). Flag first, plan gate second: rollout state is
// never folded into FEATURE_REQUIREMENTS.
import { SurfaceGate } from '@/components/common/surface-gate'
import { LegacyTickerRedirect } from '@/components/common/legacy-ticker-redirect'
import { FLAGS } from '@/services/analytics/feature-flags'
import { debugToolsEnabled } from '@/ett-debug-tools'

// ─── Lazy-loaded features ───
// Each route-level component is code-split so users only download what they visit.

const DashboardPage = lazy(() =>
  import('@/features/dashboard/dashboard-page').then((m) => ({ default: m.DashboardPage })),
)
const HistoryPage = lazy(() =>
  import('@/features/history').then((m) => ({ default: m.HistoryPage })),
)
const PlaybookPage = lazy(() =>
  import('@/features/playbook').then((m) => ({ default: m.PlaybookPage })),
)
const JournalPage = lazy(() =>
  import('@/features/journal').then((m) => ({ default: m.JournalPage })),
)
const StudioPage = lazy(() =>
  import('@/features/chart-sketch-studio').then((m) => ({ default: m.StudioPage })),
)

// Research
const ResearchLayout = lazy(() =>
  import('@/features/research').then((m) => ({ default: m.ResearchLayout })),
)
const OverviewPage = lazy(() =>
  import('@/features/research').then((m) => ({ default: m.OverviewPage })),
)
const ChartPage = lazy(() => import('@/features/research').then((m) => ({ default: m.ChartPage })))
const SeasonalityPage = lazy(() =>
  import('@/features/research').then((m) => ({ default: m.SeasonalityPage })),
)
const FinancialsPage = lazy(() =>
  import('@/features/research').then((m) => ({ default: m.FinancialsPage })),
)
const GraphsPage = lazy(() =>
  import('@/features/research').then((m) => ({ default: m.GraphsPage })),
)
const ComparisonPage = lazy(() =>
  import('@/features/research').then((m) => ({ default: m.ComparisonPage })),
)
const NewsPage = lazy(() => import('@/features/research').then((m) => ({ default: m.NewsPage })))
const FilingsPage = lazy(() =>
  import('@/features/research').then((m) => ({ default: m.FilingsPage })),
)
const FilingDetailPage = lazy(() =>
  import('@/features/research').then((m) => ({ default: m.FilingDetailPage })),
)
const FilingComparePage = lazy(() =>
  import('@/features/research').then((m) => ({ default: m.FilingComparePage })),
)
const NotesPage = lazy(() => import('@/features/research').then((m) => ({ default: m.NotesPage })))
const EarningsPage = lazy(() =>
  import('@/features/research').then((m) => ({ default: m.EarningsPage })),
)
const DilutionPage = lazy(() =>
  import('@/features/research').then((m) => ({ default: m.DilutionPage })),
)
const TranscriptsPage = lazy(() =>
  import('@/features/research').then((m) => ({ default: m.TranscriptsPage })),
)
const TranscriptDetailPage = lazy(() =>
  import('@/features/research').then((m) => ({ default: m.TranscriptDetailPage })),
)

// Account
const AccountLayout = lazy(() =>
  import('@/features/account').then((m) => ({ default: m.AccountLayout })),
)
const ProfilePage = lazy(() =>
  import('@/features/account').then((m) => ({ default: m.ProfilePage })),
)
const PasswordPage = lazy(() =>
  import('@/features/account').then((m) => ({ default: m.PasswordPage })),
)
const ChangeEmailPage = lazy(() =>
  import('@/features/account').then((m) => ({ default: m.ChangeEmailPage })),
)
const FeedbackPage = lazy(() =>
  import('@/features/account').then((m) => ({ default: m.FeedbackPage })),
)
const AccountBillingPage = lazy(() =>
  import('@/features/account/pages/billing-page').then((m) => ({ default: m.BillingPage })),
)
const NotificationsPage = lazy(() =>
  import('@/features/account').then((m) => ({ default: m.NotificationsPage })),
)
const CommunicationsPage = lazy(() =>
  import('@/features/account').then((m) => ({ default: m.CommunicationsPage })),
)
const ProStatusSurveyPage = lazy(() =>
  import('@/features/account').then((m) => ({ default: m.ProStatusSurveyPage })),
)
const AdminPage = lazy(() => import('@/features/account').then((m) => ({ default: m.AdminPage })))
const SkillsPage = lazy(() => import('@/features/skills').then((m) => ({ default: m.SkillsPage })))

// Chat pop-out
const ChatPopoutPage = lazy(() =>
  import('@/components/chat/chat-popout').then((m) => ({ default: m.ChatPopout })),
)

// Other features
const DeepResearchPage = lazy(() =>
  import('@/features/deep-research').then((m) => ({ default: m.DeepResearchPage })),
)
const SharedReportPage = lazy(() =>
  import('@/features/deep-research').then((m) => ({ default: m.SharedReportPage })),
)
const DemoPage = lazy(() => import('@/features/demo/demo-page'))
const ExportsPage = lazy(() =>
  import('@/features/exports').then((m) => ({ default: m.ExportsPage })),
)
// Dev-only gallery of every upgrade pitch, the legacy app's /marketing-modals.
const UpgradeShowcasePage = lazy(() =>
  import('@/features/upgrade/upgrade-showcase-page').then((m) => ({
    default: m.UpgradeShowcasePage,
  })),
)
// Dev-only review of the Playbook's card pictures, a folder per play.
const PlaybookVisualsPage = lazy(() =>
  import('@/features/chart-sketch-studio/dev/playbook-visuals-page').then((m) => ({
    default: m.PlaybookVisualsPage,
  })),
)
// Dev-only: every backtest node type on one canvas, measured by
// e2e/node-gallery.spec.ts.
const NodeGalleryPage = lazy(() =>
  import('@/features/backtest/dev/node-gallery-page').then((m) => ({
    default: m.NodeGalleryPage,
  })),
)
const LearnPage = lazy(() =>
  import('@/features/learn/learn-page').then((m) => ({ default: m.LearnPage })),
)
const ImportPage = lazy(() =>
  import('@/features/import/import-page').then((m) => ({ default: m.ImportPage })),
)

// Billing
const PlanSelectionPage = lazy(() =>
  import('@/features/billing').then((m) => ({ default: m.PlanSelectionPage })),
)
const BillingSuccessPage = lazy(() =>
  import('@/features/billing').then((m) => ({ default: m.BillingSuccessPage })),
)
const RecertifyPage = lazy(() =>
  import('@/features/billing').then((m) => ({ default: m.RecertifyPage })),
)
const ProfessionalSwitchPage = lazy(() =>
  import('@/features/billing').then((m) => ({ default: m.ProfessionalSwitchPage })),
)

// Backtest
const BacktestLayout = lazy(() => import('@/features/backtest/backtest-layout'))
const StrategiesPage = lazy(() => import('@/features/backtest/pages/strategies-page'))
const BacktesterHomeRedirect = lazy(
  () => import('@/features/backtest/pages/backtester-home-redirect'),
)
const EditorPage = lazy(() => import('@/features/backtest/pages/editor-page'))
const StrategyStatsPage = lazy(() => import('@/features/backtest/pages/strategy-stats-page'))
const VersionHistoryPage = lazy(() => import('@/features/backtest/pages/version-history-page'))
const RunComparisonPage = lazy(() => import('@/features/backtest/pages/run-comparison-page'))
const RunReportRedirect = lazy(() => import('@/features/backtest/pages/run-report-redirect'))
const ReportLatestRedirect = lazy(() => import('@/features/backtest/pages/report-latest-redirect'))
const LegacyBacktestRedirect = lazy(
  () => import('@/features/backtest/pages/legacy-backtest-redirect'),
)
const ExecutionResultPage = lazy(() => import('@/features/backtest/pages/execution-result-page'))
const ReportWorkspacePage = lazy(() =>
  import('@/features/backtest/report/report-workspace-page').then((m) => ({
    default: m.ReportWorkspacePage,
  })),
)
const RunWorkspacePage = lazy(() =>
  import('@/features/backtest/report/run-workspace-page').then((m) => ({
    default: m.RunWorkspacePage,
  })),
)
const ComparePage = lazy(() => import('@/features/backtest/pages/compare-page'))
const RecentRunsPage = lazy(() => import('@/features/backtest/pages/recent-runs-page'))
const RunModeRedirect = lazy(() => import('@/features/backtest/pages/run-mode-redirect'))
const EditorRedirect = lazy(() => import('@/features/backtest/pages/editor-redirect'))
const StrategyWorkspaceLayout = lazy(() =>
  import('@/features/backtest/components/workspace/strategy-workspace-layout').then((m) => ({
    default: m.StrategyWorkspaceLayout,
  })),
)

// ─── Shared loading / error fallbacks ───

function PageSkeleton() {
  return (
    <div className="flex h-full w-full items-center justify-center bg-background text-muted-foreground">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
    </div>
  )
}

function NotFoundPage() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="text-center">
        <h1 className="text-6xl font-bold text-foreground">404</h1>
        <p className="mt-2 text-muted-foreground">Page not found</p>
      </div>
    </div>
  )
}

/** Wraps a lazy element with Suspense + ErrorBoundary */
function Lazy({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageSkeleton />}>{children}</Suspense>
    </ErrorBoundary>
  )
}

// ─── Router ───

const router = createBrowserRouter(
  [
    // Public auth routes
    { path: '/login', element: <LoginPage /> },
    { path: '/password-reset', element: <PasswordResetPage /> },
    { path: '/verify-email', element: <VerifyEmailPage /> },
    // A shared Deep Research report: the link's token is the credential, so no
    // sign-in and no app layout (a signed-out recipient can read it).
    {
      path: '/shared/report/:token',
      element: (
        <Lazy>
          <SharedReportPage />
        </Lazy>
      ),
    },
    // Beta-locked lands signed-in beta users without a live subscription. Only exists in
    // beta builds — elsewhere it falls through to 404. Deliberately NOT behind AuthGuard:
    // AuthGuard is what redirects here, so guarding it would loop.
    ...(BETA ? [{ path: '/beta-locked', element: <BetaLockedPage /> }] : []),

    // Beta preview: no self-serve registration — access is provisioned per-user, so
    // /register redirects to /login rather than showing the sign-up form.
    {
      path: '/register',
      element: BETA ? <Navigate to="/login" replace /> : <RegisterPage />,
    },

    // Billing flow (separate guard, no app layout). Excluded entirely in beta —
    // there is no billing in the preview.
    ...(!BETA
      ? [
          // Branded Cobra Premier funnel (public — sets the intent marker pre-auth).
          { path: '/cobra', element: <CobraPage /> },
          {
            path: '/billing',
            element: (
              <BillingGuard>
                <Lazy>
                  <PlanSelectionPage />
                </Lazy>
              </BillingGuard>
            ),
          },
          {
            path: '/billing/success',
            element: (
              <Lazy>
                <BillingSuccessPage />
              </Lazy>
            ),
          },
          {
            path: '/billing/recertify',
            element: (
              <Lazy>
                <RecertifyPage />
              </Lazy>
            ),
          },
          {
            path: '/billing/switch',
            element: (
              <Lazy>
                <ProfessionalSwitchPage />
              </Lazy>
            ),
          },
        ]
      : []),

    // Import route (authenticated, no app layout — standalone page)
    {
      path: '/import',
      element: (
        <AuthGuard>
          <Lazy>
            <ImportPage />
          </Lazy>
        </AuthGuard>
      ),
    },

    // Chat pop-out window (authenticated, standalone — no nav/sidebar)
    {
      path: '/chat-popout',
      element: (
        <AuthGuard>
          <Lazy>
            <ChatPopoutPage />
          </Lazy>
        </AuthGuard>
      ),
    },

    // Authenticated routes with AppLayout
    {
      element: (
        <AuthGuard>
          <AppLayout />
        </AuthGuard>
      ),
      children: [
        {
          path: '/dashboard',
          element: (
            <Lazy>
              <DashboardPage />
            </Lazy>
          ),
        },
        {
          path: '/university/*',
          element: (
            <Lazy>
              <LearnPage />
            </Lazy>
          ),
        },
        // Back-compat: old /learn links redirect to the renamed route.
        {
          path: '/learn',
          element: <Navigate to="/university" replace />,
        },
        {
          path: '/research/:tickerId',
          element: (
            <Lazy>
              <ResearchLayout />
            </Lazy>
          ),
          children: [
            { index: true, element: <Navigate to="overview" replace /> },
            {
              path: 'overview',
              element: (
                <Lazy>
                  <OverviewPage />
                </Lazy>
              ),
            },
            {
              path: 'chart',
              element: (
                <Lazy>
                  <ChartPage />
                </Lazy>
              ),
            },
            {
              path: 'chart/seasonality',
              element: (
                <Lazy>
                  <SeasonalityPage />
                </Lazy>
              ),
            },
            {
              path: 'financials',
              element: (
                <Lazy>
                  <FinancialsPage />
                </Lazy>
              ),
            },
            {
              path: 'chart/graphs',
              element: (
                <Lazy>
                  <GraphsPage />
                </Lazy>
              ),
            },
            {
              path: 'chart/comparison',
              element: (
                <Lazy>
                  <ComparisonPage />
                </Lazy>
              ),
            },
            // Graphs moved under Chart (2026-09-23); keep old links working.
            { path: 'financials/graphs', element: <Navigate to="../chart/graphs" replace /> },
            {
              path: 'news',
              element: (
                <Lazy>
                  <NewsPage />
                </Lazy>
              ),
            },
            {
              path: 'filings',
              element: (
                <Lazy>
                  <FilingsPage />
                </Lazy>
              ),
            },
            {
              path: 'filings/:filingId',
              element: (
                <Lazy>
                  <FilingDetailPage />
                </Lazy>
              ),
            },
            {
              path: 'filings/:baseId/compare/:compareId',
              element: (
                <Lazy>
                  <FilingComparePage />
                </Lazy>
              ),
            },
            {
              path: 'notes',
              element: (
                <SurfaceGate flag={FLAGS.NOTEBOOK}>
                  <Lazy>
                    <NotesPage />
                  </Lazy>
                </SurfaceGate>
              ),
            },
            {
              path: 'earnings',
              element: (
                <Lazy>
                  <EarningsPage />
                </Lazy>
              ),
            },
            {
              path: 'dilution',
              element: (
                <Lazy>
                  <DilutionPage />
                </Lazy>
              ),
            },
            {
              path: 'transcripts',
              element: (
                <Lazy>
                  <TranscriptsPage />
                </Lazy>
              ),
            },
            {
              path: 'transcripts/:fiscalYear/:quarter',
              element: (
                <Lazy>
                  <TranscriptDetailPage />
                </Lazy>
              ),
            },
          ],
        },
        {
          path: '/history',
          element: (
            <Lazy>
              <HistoryPage />
            </Lazy>
          ),
        },
        {
          path: '/journal',
          element: (
            <SurfaceGate flag={FLAGS.JOURNAL}>
              <Lazy>
                <JournalPage />
              </Lazy>
            </SurfaceGate>
          ),
        },
        {
          path: '/playbook/:playId?',
          element: (
            <SurfaceGate flag={FLAGS.PLAYBOOK}>
              <Lazy>
                <PlaybookPage />
              </Lazy>
            </SurfaceGate>
          ),
        },
        {
          // The chart-sketch studio: draw a move, get candles, export a picture.
          // Same audience as the Playbook, whose card pictures link here.
          path: '/studio',
          element: (
            <SurfaceGate flag={FLAGS.PLAYBOOK}>
              <Lazy>
                <StudioPage />
              </Lazy>
            </SurfaceGate>
          ),
        },
        {
          path: '/deep-research/:sessionId?',
          element: (
            <SurfaceGate flag={FLAGS.DEEP_RESEARCH}>
              <Lazy>
                <DeepResearchPage />
              </Lazy>
            </SurfaceGate>
          ),
        },
        {
          path: '/deep-dive',
          element: <Navigate to="/deep-research" replace />,
        },
        // The tool is the Backtester, so its route says so (2026-09-03). The
        // old /backtest addresses redirect below, subpath and query intact.
        // The legacy addresses are gated too, or /backtest walks past the
        // flag into the redirect.
        {
          path: '/backtest',
          element: (
            <SurfaceGate flag={FLAGS.BACKTESTER}>
              <LegacyBacktestRedirect />
            </SurfaceGate>
          ),
        },
        {
          path: '/backtest/*',
          element: (
            <SurfaceGate flag={FLAGS.BACKTESTER}>
              <LegacyBacktestRedirect />
            </SurfaceGate>
          ),
        },
        {
          path: '/backtester',
          element: (
            <SurfaceGate flag={FLAGS.BACKTESTER}>
              <Lazy>
                <BacktestLayout />
              </Lazy>
            </SurfaceGate>
          ),
          children: [
            // The home is the Strategies page: the Learn rail beside the
            // folder grid. The Overview folded into it, and New Strategy makes
            // the default crossover in one click, so both old addresses land
            // here (Chris, 2026-10-06). See
            // docs/superpowers/specs/2026-10-04-backtester-landing-and-home-merge-design.md.
            { index: true, element: <StrategiesPage /> },
            { path: 'strategies', element: <BacktesterHomeRedirect /> },
            { path: 'new', element: <BacktesterHomeRedirect /> },
            { path: 'compare', element: <ComparePage /> },
            { path: 'recent-runs/:executionId?', element: <RecentRunsPage /> },
            { path: 'editor', element: <EditorRedirect /> },
            { path: 'editor/:id', element: <EditorRedirect /> },
            // The strategy workspace: one document, three modes on a bottom
            // strip. Build, Run and Report replace the hub-plus-pages shape.
            // See docs/superpowers/specs/2026-08-31-backtest-strategy-workspace-design.md.
            {
              path: 'strategy/:id',
              element: (
                <Lazy>
                  <StrategyWorkspaceLayout />
                </Lazy>
              ),
              children: [
                { index: true, element: <Navigate to="build" replace /> },
                { path: 'build', element: <EditorPage /> },
                // Running moved into Build (2026-10-05); old Run links land there.
                { path: 'run', element: <RunModeRedirect /> },
                // Report is always a run's report: the bare address opens the
                // latest run (Chris, 2026-09-06), never the all-runs page.
                { path: 'report', element: <ReportLatestRedirect /> },
                { path: 'report/:executionId', element: <RunWorkspacePage /> },
              ],
            },
            { path: 'strategy/:id/compare', element: <RunComparisonPage /> },
            { path: 'strategy/:id/stats', element: <StrategyStatsPage /> },
            { path: 'strategy/:id/stats-v2', element: <ReportWorkspacePage /> },
            { path: 'version-history', element: <VersionHistoryPage /> },
            // Legacy run-report addresses. Both now mean the Report run route;
            // the redirect keeps bookmarks and companion actions working.
            {
              path: 'strategy/:strategyId/run/:executionId',
              element: <RunReportRedirect />,
            },
            {
              path: 'strategy/:strategyId/run/:executionId/workspace',
              element: <RunReportRedirect />,
            },
            {
              path: 'strategy/:strategyId/run/:executionId/result/:ticker/:targetDate',
              element: <ExecutionResultPage />,
            },
          ],
        },
        { path: '/notes', element: <Navigate to="/dashboard" replace /> },
        // Copy review for the plan gates. Gated on the SAME flag as the debug
        // sidebar that links here, not on `import.meta.env.DEV`: that flag is
        // false in every build, staging included, so the route used to be
        // compiled out while staging still rendered the link to it. The result
        // was a 404 with nothing in any log, because the miss never left the
        // browser.
        ...(debugToolsEnabled
          ? [
              {
                path: '/dev/upgrade-prompts',
                element: (
                  <Lazy>
                    <UpgradeShowcasePage />
                  </Lazy>
                ),
              },
              {
                path: '/dev/playbook-visuals',
                element: (
                  <Lazy>
                    <PlaybookVisualsPage />
                  </Lazy>
                ),
              },
            ]
          : []),
        // The node gallery is also the page the layout check measures
        // (e2e/node-gallery.spec.ts), and e2e mode swaps the debug tools for a
        // no-op (vite.config.ts), so it opens in e2e mode too.
        ...(debugToolsEnabled || import.meta.env.MODE === 'e2e'
          ? [
              {
                path: '/dev/node-gallery',
                element: (
                  <Lazy>
                    <NodeGalleryPage />
                  </Lazy>
                ),
              },
            ]
          : []),
        // Hidden (no route, no menu entry) until a Calendly page is configured.
        ...(CALENDLY.demoUrl
          ? [
              {
                path: '/demo',
                element: (
                  <Lazy>
                    <DemoPage />
                  </Lazy>
                ),
              },
            ]
          : []),
        {
          path: '/exports',
          element: (
            <Lazy>
              <ExportsPage />
            </Lazy>
          ),
        },
        {
          // Tags view renders INSIDE HistoryPage (same sidebar + chrome);
          // the page switches content on this path.
          path: '/history/tags',
          element: (
            <Lazy>
              <HistoryPage />
            </Lazy>
          ),
        },
        { path: '/tags', element: <Navigate to="/history/tags" replace /> },
        {
          path: '/account',
          element: (
            <Lazy>
              <AccountLayout />
            </Lazy>
          ),
          children: [
            { index: true, element: <Navigate to="/account/profile" replace /> },
            { path: 'general', element: <Navigate to="/account/profile" replace /> },
            { path: 'subscription', element: <Navigate to="/account/billing" replace /> },
            {
              path: 'profile',
              element: (
                <Lazy>
                  <ProfilePage />
                </Lazy>
              ),
            },
            {
              path: 'password',
              element: (
                <Lazy>
                  <PasswordPage />
                </Lazy>
              ),
            },
            {
              // Route stays in every build; the entry points are hidden in beta (see
              // account-sidebar / profile-page). Deep-linking here in beta is harmless.
              path: 'email',
              element: (
                <Lazy>
                  <ChangeEmailPage />
                </Lazy>
              ),
            },
            {
              path: 'billing',
              element: (
                <Lazy>
                  <AccountBillingPage />
                </Lazy>
              ),
            },
            {
              path: 'notifications',
              element: (
                <Lazy>
                  <NotificationsPage />
                </Lazy>
              ),
            },
            {
              path: 'communications',
              element: (
                <Lazy>
                  <CommunicationsPage />
                </Lazy>
              ),
            },
            {
              path: 'pro-status-survey',
              element: (
                <Lazy>
                  <ProStatusSurveyPage />
                </Lazy>
              ),
            },
            {
              path: 'feedback',
              element: (
                <Lazy>
                  <FeedbackPage />
                </Lazy>
              ),
            },
            {
              path: 'skills',
              element: (
                <Lazy>
                  <SkillsPage />
                </Lazy>
              ),
            },
            {
              path: 'admin',
              element: (
                <Lazy>
                  <AdminPage />
                </Lazy>
              ),
            },
          ],
        },
      ],
    },

    // Redirects
    { path: '/', element: <HomeRedirect /> },

    // Legacy route redirects
    { path: '/top-lists', element: <HomeRedirect /> },
    { path: '/overview/*', element: <HomeRedirect /> },
    { path: '/news/*', element: <HomeRedirect /> },
    { path: '/financials/:tickerId', element: <LegacyTickerRedirect tab="financials" /> },
    { path: '/SEC/:tickerId', element: <LegacyTickerRedirect tab="filings" /> },

    // 404
    { path: '/404', element: <NotFoundPage /> },
    { path: '*', element: <Navigate to="/404" replace /> },
  ],
  {
    future: {
      v7_relativeSplatPath: true,
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_skipActionErrorRevalidation: true,
    },
  },
)

setAppRouter(router)

// Customer.io in-app page rules need the current route. It sends nothing until a user is
// identified and drops repeats, so every router state update can feed it.
setCustomerIoPath(router.state.location.pathname + router.state.location.search)
router.subscribe(({ location }) => setCustomerIoPath(location.pathname + location.search))

export default function App() {
  // Drag-and-drop lives here, above the router, so it mounts once for the life of
  // the tab. It used to sit in AppLayout, which AuthGuard can unmount and remount;
  // the second provider's MultiBackend then set up before the first tore down and
  // threw "Cannot have two MultiBackends at the same time" (prod, 2026-09-29/30).
  return (
    <DndProvider manager={dndManager}>
      <RouterProvider router={router} future={{ v7_startTransition: true }} />
    </DndProvider>
  )
}
